import React, { useEffect } from 'react';
import { Ico, Icons } from './Icons';

export function MatchUpdateForm({ 
  match, 
  t1, 
  t2, 
  scoreForm, 
  setScoreForm, 
  addSet, 
  removeSet, 
  incSet, 
  decSet, 
  updSet, 
  saveScore, 
  savingScore, 
  scoreError 
}) {
  // Auto-calculate winner based on score when status changes to completed
  useEffect(() => {
    if (scoreForm.status === 'completed' && !scoreForm.specialResult && scoreForm.sets.length > 0) {
      // Calculate sets won by each player
      let p1SetsWon = 0;
      let p2SetsWon = 0;
      
      scoreForm.sets.forEach(set => {
        if (set.p1 > set.p2) {
          p1SetsWon++;
        } else if (set.p2 > set.p1) {
          p2SetsWon++;
        }
      });
      
      // Determine winner based on sets won (best of 3 or best of 5)
      // In tennis, you need to win majority of sets
      if (p1SetsWon > p2SetsWon) {
        setScoreForm(p => ({ ...p, winner: match.player1 }));
      } else if (p2SetsWon > p1SetsWon) {
        setScoreForm(p => ({ ...p, winner: match.player2 }));
      }
    }
  }, [scoreForm.status, scoreForm.sets, scoreForm.specialResult, match.player1, match.player2, setScoreForm]);
  return (
    <div>
      {/* Match Header */}
      <div style={{ background: 'linear-gradient(135deg, #00205B 0%, #0A2E6E 100%)', borderRadius: 12, padding: '16px', marginBottom: 16, textAlign: 'center' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(189,255,0,0.7)', letterSpacing: 1, marginBottom: 6 }}>{match.round}</div>
        
        {/* Check if doubles (team names contain ' / ') */}
        {t1.name.includes(' / ') && t2.name.includes(' / ') ? (
          // Doubles format: 2x2 grid
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', fontFamily: "'Outfit', sans-serif", textAlign: 'right', flex: 1 }}>{t1.name.split(' / ')[0]}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>vs</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', fontFamily: "'Outfit', sans-serif", textAlign: 'left', flex: 1 }}>{t2.name.split(' / ')[0]}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', fontFamily: "'Outfit', sans-serif", textAlign: 'right', flex: 1 }}>{t1.name.split(' / ')[1]}</div>
              <div style={{ width: 20 }} />
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', fontFamily: "'Outfit', sans-serif", textAlign: 'left', flex: 1 }}>{t2.name.split(' / ')[1]}</div>
            </div>
          </div>
        ) : (
          // Singles format: single line
          <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif" }}>
            {t1.name} <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: '0 8px' }}>vs</span> {t2.name}
          </div>
        )}
      </div>

      {match.player1 && match.player2 && (
        <>
          {/* Score Entry */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label className="form-label" style={{ margin: 0 }}>Score</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" onClick={addSet} style={{ padding: '4px 10px', fontSize: 10, fontWeight: 700, background: '#00205B', color: '#BDFF00', border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>+ Set</button>
                {scoreForm.sets.length > 1 && <button type="button" onClick={removeSet} style={{ padding: '4px 10px', fontSize: 10, fontWeight: 700, background: '#FFEBEE', color: '#E53935', border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>− Set</button>}
              </div>
            </div>
            
            {/* Compact Table Layout - Sets as Rows */}
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #EEF1F6', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F8F9FC' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#8892A6', borderBottom: '1px solid #EEF1F6' }}>SET</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#8892A6', borderBottom: '1px solid #EEF1F6', borderLeft: '1px solid #EEF1F6' }}>{t1.name}</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#8892A6', borderBottom: '1px solid #EEF1F6', borderLeft: '1px solid #EEF1F6' }}>{t2.name}</th>
                  </tr>
                </thead>
                <tbody>
                  {scoreForm.sets.map((st, i) => (
                    <tr key={i} style={{ borderBottom: i < scoreForm.sets.length - 1 ? '1px solid #EEF1F6' : 'none' }}>
                      <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 700, color: '#00205B' }}>Set {i + 1}</td>
                      {/* Player 1 Score */}
                      <td style={{ padding: '8px', textAlign: 'center', borderLeft: '1px solid #EEF1F6' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'stretch', background: '#F8F9FC', borderRadius: 6, overflow: 'hidden', border: '1px solid #D8DDE8' }}>
                          <button type="button" onClick={() => decSet(i, 'p1')} style={{ width: 28, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', borderRight: '1px solid #D8DDE8', cursor: 'pointer', fontSize: 16, color: '#5F6B80', fontWeight: 700 }}>−</button>
                          <span style={{ minWidth: 36, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: '#00205B', fontFamily: "'Outfit', sans-serif", background: '#fff' }}>{st.p1}</span>
                          <button type="button" onClick={() => incSet(i, 'p1')} style={{ width: 28, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', borderLeft: '1px solid #D8DDE8', cursor: 'pointer', fontSize: 16, color: '#5F6B80', fontWeight: 700 }}>+</button>
                        </div>
                      </td>
                      {/* Player 2 Score */}
                      <td style={{ padding: '8px', textAlign: 'center', borderLeft: '1px solid #EEF1F6' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'stretch', background: '#F8F9FC', borderRadius: 6, overflow: 'hidden', border: '1px solid #D8DDE8' }}>
                          <button type="button" onClick={() => decSet(i, 'p2')} style={{ width: 28, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', borderRight: '1px solid #D8DDE8', cursor: 'pointer', fontSize: 16, color: '#5F6B80', fontWeight: 700 }}>−</button>
                          <span style={{ minWidth: 36, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: '#00205B', fontFamily: "'Outfit', sans-serif", background: '#fff' }}>{st.p2}</span>
                          <button type="button" onClick={() => incSet(i, 'p2')} style={{ width: 28, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', borderLeft: '1px solid #D8DDE8', cursor: 'pointer', fontSize: 16, color: '#5F6B80', fontWeight: 700 }}>+</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Status Selection */}
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Match Status</label>
            <div style={{ display: 'flex', gap: 6, background: '#F8F9FC', padding: 4, borderRadius: 8 }}>
              {['scheduled', 'live', 'completed'].map(status => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setScoreForm(p => ({ ...p, status }))}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    background: scoreForm.status === status ? '#00205B' : 'transparent',
                    color: scoreForm.status === status ? '#BDFF00' : '#5F6B80',
                    fontWeight: 700,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    transition: 'all 0.2s',
                    fontFamily: "'Outfit', sans-serif"
                  }}
                >
                  {status === 'live' && scoreForm.status === status && <span style={{ marginRight: 4 }}>●</span>}
                  {status}
                </button>
              ))}
            </div>
          </div>

          {/* Winner Selection (only if completed) */}
          {scoreForm.status === 'completed' && (
            <div style={{ marginBottom: 16 }}>
              <label className="form-label">Winner</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setScoreForm(p => ({ ...p, winner: match.player1 }))}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: scoreForm.winner === match.player1 ? '2px solid #43A047' : '2px solid #EEF1F6',
                    borderRadius: 8,
                    cursor: 'pointer',
                    background: scoreForm.winner === match.player1 ? '#E8F5E9' : '#fff',
                    color: scoreForm.winner === match.player1 ? '#43A047' : '#5F6B80',
                    fontWeight: 700,
                    fontSize: 13,
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6
                  }}
                >
                  {scoreForm.winner === match.player1 && <Ico d={Icons.trophy} size={14} color="#43A047" />}
                  {t1.name}
                </button>
                <button
                  type="button"
                  onClick={() => setScoreForm(p => ({ ...p, winner: match.player2 }))}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: scoreForm.winner === match.player2 ? '2px solid #43A047' : '2px solid #EEF1F6',
                    borderRadius: 8,
                    cursor: 'pointer',
                    background: scoreForm.winner === match.player2 ? '#E8F5E9' : '#fff',
                    color: scoreForm.winner === match.player2 ? '#43A047' : '#5F6B80',
                    fontWeight: 700,
                    fontSize: 13,
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6
                  }}
                >
                  {scoreForm.winner === match.player2 && <Ico d={Icons.trophy} size={14} color="#43A047" />}
                  {t2.name}
                </button>
              </div>
            </div>
          )}

          {/* Special Result */}
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Special Result (Optional)</label>
            <select className="form-input" value={scoreForm.specialResult || ''} onChange={e => setScoreForm(p => ({ ...p, specialResult: e.target.value || '', status: e.target.value ? 'completed' : p.status }))}>
              <option value="">Normal match</option>
              <option value="walkover">Walkover (W/O)</option>
              <option value="retirement">Retirement (RET)</option>
              <option value="disqualification">Disqualification (DQ)</option>
            </select>
            {scoreForm.specialResult && !scoreForm.winner && <div style={{ fontSize: 11, color: '#E53935', marginTop: 4, fontWeight: 600 }}>⚠️ Select a winner above</div>}
          </div>
        </>
      )}

      {/* Schedule Section */}
      <div style={{ background: '#F8F9FC', borderRadius: 10, padding: 12, marginBottom: 16 }}>
        <div className="form-label" style={{ marginBottom: 8 }}>Schedule Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <label className="form-label">Date</label>
            <input className="form-input" type="date" value={scoreForm.date} onChange={e => setScoreForm(p => ({ ...p, date: e.target.value }))} />
          </div>
          <div>
            <label className="form-label">Time</label>
            <input className="form-input" type="time" value={scoreForm.time} onChange={e => setScoreForm(p => ({ ...p, time: e.target.value }))} />
          </div>
        </div>
        <div>
          <label className="form-label">Court</label>
          <input className="form-input" value={scoreForm.court} onChange={e => setScoreForm(p => ({ ...p, court: e.target.value }))} placeholder="e.g. Center Court" />
        </div>
      </div>

      {/* Error Message */}
      {scoreError && (
        <div style={{ padding: '10px 12px', background: '#FFEBEE', border: '1px solid #FFCDD2', borderRadius: 8, color: '#E53935', fontSize: 12, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>⚠️</span> {scoreError}
        </div>
      )}

      {/* Save Button */}
      <button
        onClick={saveScore}
        disabled={savingScore}
        style={{
          width: '100%',
          padding: '12px',
          background: savingScore ? '#8892A6' : '#00205B',
          color: '#BDFF00',
          border: 'none',
          borderRadius: 8,
          fontWeight: 700,
          fontSize: 13,
          cursor: savingScore ? 'not-allowed' : 'pointer',
          letterSpacing: 0.5,
          fontFamily: "'Outfit', sans-serif",
          transition: 'all 0.2s'
        }}
      >
        {savingScore ? 'Saving...' : 'Save Match'}
      </button>
    </div>
  );
}
